import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  TourProvider,
  useTour,
  TourAlertDialog,
  type TourCompleteReason,
  type TourStep,
} from '@/components/ui/Tour';
import type { UserPreferences } from '@court-sync/shared';
import { getPageTourDefinition, type PageTourKey } from '@/lib/tours/registry';
import { useAuthStore } from '@/store/auth';
import { userService } from '@/services/user';
import {
  beginTourSession,
  clearTourSession,
  getActiveTourSession,
  type UpsellTourKind,
} from '@/lib/tours/tourSession';

let persistPageTourFn: ((key: string) => Promise<void>) | null = null;

export interface StartPageTourOptions {
  /** Manual replay from help popover — skip the welcome dialog. */
  skipWelcomeDialog?: boolean;
}

export interface StartTourWithCompletionKeyOptions {
  onFinish?: () => void;
  skipWelcomeDialog?: boolean;
}

interface WelcomePromptState {
  title: string;
  description: string;
  icon: LucideIcon;
  onConfirm: () => void;
}

interface PageTourContextValue {
  startPageTour: (key: PageTourKey, options?: StartPageTourOptions) => void;
  promptAutoPageTour: (key: PageTourKey, launch?: () => void) => void;
  startTourWithCompletionKey: (
    completionKey: string,
    steps: TourStep[],
    options?: StartTourWithCompletionKeyOptions,
  ) => void;
  beginUpsellTourSession: (kind: UpsellTourKind) => void;
  beginDashboardTourSession: (preferences: Partial<UserPreferences>) => void;
}

const PageTourContext = React.createContext<PageTourContextValue | null>(null);

export function usePageTourActions(): PageTourContextValue {
  const ctx = React.useContext(PageTourContext);
  if (!ctx) {
    return {
      startPageTour: () => {},
      promptAutoPageTour: () => {},
      startTourWithCompletionKey: () => {},
      beginUpsellTourSession: () => {},
      beginDashboardTourSession: () => {},
    };
  }
  return ctx;
}

function PageTourHostInner({ children }: { children: React.ReactNode }) {
  const { startWithSteps, isActive } = useTour();
  const isTourActiveRef = React.useRef(isActive);
  React.useEffect(() => {
    isTourActiveRef.current = isActive;
  }, [isActive]);

  const [welcomePrompt, setWelcomePrompt] = React.useState<WelcomePromptState | null>(null);
  const [welcomeDialogOpen, setWelcomeDialogOpen] = React.useState(false);

  const persistTourComplete = React.useCallback(async (key: string) => {
    try {
      const completedPageTours = {
        ...(useAuthStore.getState().user?.preferences?.completedPageTours ?? {}),
        [key]: true,
      };
      await userService.updatePreferences({ completedPageTours });
      const cur = useAuthStore.getState().user;
      if (cur) {
        useAuthStore.setState({
          user: {
            ...cur,
            preferences: {
              ...cur.preferences,
              completedPageTours,
            },
          },
        });
      }
    } catch (error) {
      console.error('Failed to persist page tour completion:', error);
    }
  }, []);

  React.useEffect(() => {
    persistPageTourFn = persistTourComplete;
    return () => {
      persistPageTourFn = null;
    };
  }, [persistTourComplete]);

  const launchTour = React.useCallback(
    (key: PageTourKey) => {
      const definition = getPageTourDefinition(key);
      if (!definition || definition.steps.length === 0) return;
      beginTourSession({ kind: 'page', completionKey: key });
      startWithSteps(definition.steps);
    },
    [startWithSteps],
  );

  const runWithOptionalWelcome = React.useCallback(
    (
      key: PageTourKey,
      launch: () => void,
      options?: { skipWelcomeDialog?: boolean },
    ) => {
      if (options?.skipWelcomeDialog) {
        launch();
        return;
      }
      const definition = getPageTourDefinition(key);
      if (!definition) {
        launch();
        return;
      }
      setWelcomePrompt({
        title: definition.dialogTitle,
        description: definition.dialogDescription,
        icon: definition.dialogIcon,
        onConfirm: launch,
      });
      setWelcomeDialogOpen(true);
    },
    [],
  );

  const startPageTour = React.useCallback(
    (key: PageTourKey, options?: StartPageTourOptions) => {
      if (isTourActiveRef.current) return;
      runWithOptionalWelcome(key, () => launchTour(key), options);
    },
    [launchTour, runWithOptionalWelcome],
  );

  const promptAutoPageTour = React.useCallback(
    (key: PageTourKey, launch?: () => void) => {
      if (isTourActiveRef.current) return;
      runWithOptionalWelcome(key, launch ?? (() => launchTour(key)));
    },
    [launchTour, runWithOptionalWelcome],
  );

  const startTourWithCompletionKey = React.useCallback(
    (
      completionKey: string,
      steps: TourStep[],
      options?: StartTourWithCompletionKeyOptions,
    ) => {
      if (steps.length === 0) return;
      if (isTourActiveRef.current) return;

      const launch = () => {
        beginTourSession({
          kind: 'page',
          completionKey,
          onFinish: options?.onFinish,
        });
        startWithSteps(steps);
      };

      if (options?.skipWelcomeDialog !== false) {
        launch();
        return;
      }

      runWithOptionalWelcome(completionKey as PageTourKey, launch);
    },
    [runWithOptionalWelcome, startWithSteps],
  );

  const beginUpsellTourSession = React.useCallback((kind: UpsellTourKind) => {
    beginTourSession({ kind: 'upsell', upsellKind: kind });
  }, []);

  const beginDashboardTourSession = React.useCallback((preferences: Partial<UserPreferences>) => {
    beginTourSession({ kind: 'dashboard', preferences });
  }, []);

  return (
    <PageTourContext.Provider
      value={{
        startPageTour,
        promptAutoPageTour,
        startTourWithCompletionKey,
        beginUpsellTourSession,
        beginDashboardTourSession,
      }}
    >
      {children}
      {welcomePrompt ? (
        <TourAlertDialog
          isOpen={welcomeDialogOpen}
          setIsOpen={(open) => {
            setWelcomeDialogOpen(open);
            if (!open) {
              setWelcomePrompt(null);
            }
          }}
          icon={welcomePrompt.icon}
          title={welcomePrompt.title}
          description={welcomePrompt.description}
          confirmLabel="Start tour"
          cancelLabel="Maybe later"
          onConfirm={welcomePrompt.onConfirm}
          onSkip={() => setWelcomePrompt(null)}
        />
      ) : null}
    </PageTourContext.Provider>
  );
}

async function persistUpsellTourComplete(kind: UpsellTourKind): Promise<void> {
  try {
    if (kind === 'professional') {
      await userService.updatePreferences({ tourCompletedProfessionalUpgrade: true });
      const cur = useAuthStore.getState().user;
      if (cur) {
        useAuthStore.setState({
          user: {
            ...cur,
            preferences: {
              ...cur.preferences,
              tourCompletedProfessionalUpgrade: true,
            },
          },
        });
      }
      return;
    }
    if (kind === 'enterprise') {
      await userService.updatePreferences({ tourCompletedEnterpriseUpgrade: true });
      const cur = useAuthStore.getState().user;
      if (cur) {
        useAuthStore.setState({
          user: {
            ...cur,
            preferences: {
              ...cur.preferences,
              tourCompletedEnterpriseUpgrade: true,
            },
          },
        });
      }
      return;
    }
    await userService.updatePreferences({ tourCompletedClubManagement: true });
    const cur = useAuthStore.getState().user;
    if (cur) {
      useAuthStore.setState({
        user: {
          ...cur,
          preferences: { ...cur.preferences, tourCompletedClubManagement: true },
        },
      });
    }
  } catch (error) {
    console.error('Failed to persist upsell tour completion:', error);
  }
}

export function PageTourHost({ children }: { children: React.ReactNode }) {
  const handleTourComplete = React.useCallback(async (reason: TourCompleteReason) => {
    const session = getActiveTourSession();
    clearTourSession();

    if (!session) {
      return;
    }

    if (session.kind === 'page') {
      const cur = useAuthStore.getState().user;
      if (cur) {
        useAuthStore.setState({
          user: {
            ...cur,
            preferences: {
              ...cur.preferences,
              completedPageTours: {
                ...(cur.preferences?.completedPageTours ?? {}),
                [session.completionKey]: true,
              },
            },
          },
        });
      }
      if (persistPageTourFn) {
        void persistPageTourFn(session.completionKey);
      }
      if (reason === 'finished') {
        session.onFinish?.();
      }
      return;
    }

    if (session.kind === 'upsell') {
      await persistUpsellTourComplete(session.upsellKind);
      return;
    }

    if (session.kind === 'dashboard') {
      try {
        await userService.updatePreferences(session.preferences);
        const cur = useAuthStore.getState().user;
        if (cur) {
          useAuthStore.setState({
            user: {
              ...cur,
              preferences: { ...cur.preferences, ...session.preferences },
            },
          });
        }
      } catch (error) {
        console.error('Failed to persist dashboard tour completion:', error);
      }
    }
  }, []);

  return (
    <TourProvider onComplete={handleTourComplete}>
      <PageTourHostInner>{children}</PageTourHostInner>
    </TourProvider>
  );
}
